import { Popover, Transition } from "@headlessui/react";
import { ArrowUpRight, Cpu, Globe, Menu, PieChart, RotateCcw, ShieldCheck, Target, X } from "lucide-react";
import { Fragment, ReactNode, useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useActiveTab } from "./ActiveTabContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger
} from "./ui/select";

const nuqiUniverseItems = [
  {
    title: "Nuqi Gold",
    url: "https://nuqigold.com/",
    description: "Invest in gold the smart way — Nuqi Gold offers a seamless platform for fractional gold ownership backed by real assets.",
    logo: "/NuqiGold-logo.png",
    gradient: "linear-gradient(135deg, #1c0e00 0%, #3a2200 60%, #5c3800 100%)",
    bgImage: "/universe-gold.png",
  },
  {
    title: "Nuqi Sukuk",
    url: "https://nuqisukuk.com/",
    description: "Nuqi Sukuk is a fractional and ethical sukuk marketplace built to democratize access to the global Islamic fixed-income market.",
    logo: "/sukuk-logo.png",
    gradient: "linear-gradient(135deg, #001a12 0%, #003322 60%, #004d33 100%)",
    bgImage: "/sukuk.png",
  },
];

const DisclaimerModal = ({ onProceed, onCancel }: { url: string; onProceed: () => void; onCancel: () => void }) => (
  <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onCancel} />
    <div className="relative w-full max-w-md bg-[#0a0a0a] border border-[#57c0af]/30 rounded-2xl shadow-2xl overflow-hidden">
      <div
        className="px-6 py-5 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #001a12 0%, #003322 100%)" }}
      >
        <div className="absolute -right-4 -top-4 w-24 h-24 rounded-full bg-white/5" />
        <p className="text-[#57c0af] text-[10px] font-poppins uppercase tracking-[0.18em] mb-1">
          Important Notice
        </p>
        <h3 className="text-white font-poppins font-bold text-base leading-snug">
          Website Redirection
        </h3>
      </div>

      <div className="px-6 py-5 max-h-[55vh] overflow-y-auto">
        <p className="text-gray-400 text-xs leading-relaxed mb-4">
          You are about to leave this website and will be redirected to the website of another entity within the Group (the <span className="font-semibold text-gray-200">"Recipient Entity"</span>).
        </p>
        <p className="text-gray-400 text-xs leading-relaxed mb-4">
          Please note that each entity within the Group is a separate legal entity, which may operate under a different regulatory status, jurisdiction, and supervisory authority, or may not be regulated for certain activities.
        </p>
        <p className="text-gray-400 text-xs leading-relaxed mb-4">
          The products and services available on the Recipient Entity's website are provided under its own responsibility and, where applicable, its own regulatory permissions, and may not be subject to the same regulatory framework, investor protections, or client safeguards applicable to this website.
        </p>
        <p className="text-gray-400 text-xs leading-relaxed mb-4">
          Any information, content, or services available on the Recipient Entity's website are the sole responsibility of that entity. The originating entity does not make any representation, warranty, or guarantee as to the accuracy, completeness, or regulatory compliance of such content.
        </p>
        <p className="text-gray-300 text-xs font-semibold mb-2">By proceeding, you acknowledge and agree that:</p>
        <ul className="space-y-1.5 mb-2">
          {[
            "You are leaving this website and entering a separate website operated by another legal entity.",
            "Different terms and conditions, privacy policies, and regulatory protections may apply.",
            "You should independently review the Recipient Entity's disclosures, regulatory status, and terms before relying on any information or engaging in any transaction.",
          ].map((point, i) => (
            <li key={i} className="flex items-start gap-2 text-gray-400 text-xs leading-relaxed">
              <span className="mt-1.5 flex-shrink-0 w-1 h-1 rounded-full bg-[#57c0af]" />
              {point}
            </li>
          ))}
        </ul>
        <p className="text-gray-500 text-xs leading-relaxed mt-3">
          If you do not wish to proceed, please select <span className="font-semibold text-gray-300">"Cancel"</span> to remain on this website.
        </p>
      </div>

      <div className="px-6 py-4 bg-black/40 border-t border-white/10 flex items-center gap-3">
        <button
          onClick={onCancel}
          className="flex-1 py-2.5 rounded-xl border border-white/20 text-gray-300 text-sm font-semibold font-poppins hover:bg-white/5 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onProceed}
          className="flex-1 py-2.5 rounded-xl text-black text-sm font-semibold font-poppins transition-colors bg-[#57c0af] hover:bg-[#45a898]"
        >
          Proceed
        </button>
      </div>
    </div>
  </div>
);

const NuqiUniverseSidebar = ({ isOpen, onClose, navbarHeight }: { isOpen: boolean; onClose: () => void; navbarHeight: number }) => {
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);

  const handleCardClick = (e: React.MouseEvent<HTMLAnchorElement>, url: string) => {
    e.preventDefault();
    setPendingUrl(url);
  };

  const handleProceed = () => {
    if (pendingUrl) window.open(pendingUrl, "_blank", "noopener,noreferrer");
    setPendingUrl(null);
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/60 z-40 transition-opacity duration-300 ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
      />

      <div
        className={`fixed top-0 right-0 h-full z-[60] flex flex-col shadow-2xl transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"}`}
        style={{
          width: "min(360px, 100vw)",
          background: "linear-gradient(180deg, #0a1a14 0%, #050505 30%)",
        }}
      >
        {/* Hero banner */}
        <div className="flex-shrink-0 relative overflow-hidden bg-black">
          <img
            src="/wealth.png"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none select-none"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/70 pointer-events-none" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent pointer-events-none" />

          <div style={{ height: navbarHeight }} className="relative flex items-center justify-end px-5">
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 text-white transition-all"
              aria-label="Close"
            >
              <X size={15} />
            </button>
          </div>

          <div className="relative px-5 pb-5">
            <p className="text-[#57c0af] text-[10px] font-poppins uppercase tracking-[0.2em] mb-1.5">
              Explore
            </p>
            <h2 className="text-white font-poppins font-bold text-xl leading-tight drop-shadow-md">
              Nuqi Universe
            </h2>
            <p className="text-white/70 text-xs mt-1 font-poppins">
              Discover the full Nuqi ecosystem
            </p>
            <div className="mt-4 h-[2px] w-10 bg-gradient-to-r from-[#57c0af] to-[#45a898] rounded-full" />
          </div>
        </div>

        {/* Cards */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {nuqiUniverseItems.map((item, i) => (
            <a
              key={i}
              href={item.url}
              onClick={(e) => handleCardClick(e, item.url)}
              className="group block rounded-2xl overflow-hidden shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 cursor-pointer"
            >
              <div
                className="relative px-5 py-10 overflow-hidden"
                style={{ background: item.bgImage ? "transparent" : item.gradient }}
              >
                {item.bgImage && (
                  <img
                    src={item.bgImage}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
                  />
                )}
                {item.bgImage && (
                  <div className="absolute inset-0 bg-black/35 pointer-events-none" />
                )}
                <div className="relative flex items-center justify-between">
                  <img
                    src={item.logo}
                    alt={item.title}
                    className="h-7 w-auto max-w-[130px] object-contain"
                    style={{ filter: "brightness(0) invert(1)" }}
                  />
                  <div className="w-8 h-8 rounded-full bg-white/10 group-hover:bg-white/20 flex items-center justify-center transition-colors duration-200">
                    <ArrowUpRight size={14} className="text-white/70 group-hover:text-white transition-colors" />
                  </div>
                </div>
              </div>

              <div className="bg-[#0f1f18] border border-white/5 px-5 py-4">
                <p className="font-bold text-white font-poppins text-sm mb-1">{item.title}</p>
                <p className="text-gray-400 text-xs leading-relaxed">{item.description}</p>
                <div className="mt-3 flex items-center gap-1 text-[11px] font-semibold text-[#57c0af] font-poppins group-hover:gap-2 transition-all duration-150">
                  Visit site <ArrowUpRight size={11} />
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-5 py-3.5 border-t border-white/10 bg-black/40 flex items-center justify-between">
          <img src="/white-in-logo-cropped.png" alt="Nuqi" className="h-4 object-contain opacity-25" />
          <span className="text-[10px] text-gray-600 font-poppins tracking-widest uppercase">
            More coming soon
          </span>
        </div>
      </div>

      {pendingUrl && (
        <DisclaimerModal
          url={pendingUrl}
          onProceed={handleProceed}
          onCancel={() => setPendingUrl(null)}
        />
      )}
    </>
  );
};

const DigitalWealthTabTitle = [
  {
    title: "Secure and Regulated",
    icon: ShieldCheck,
    themeColor: "#57c0af",
    link: "/",
  },
  {
    title: "Tailored Equity Solutions",
    icon: Target,
    themeColor: "orange",
    link: "/",
  },
  {
    title: "Portfolio Rebalancing",
    icon: RotateCcw,
    themeColor: "#45DFB1",
    link: "/",
  },
  {
    title: "Asset Diversification",
    icon: PieChart,
    themeColor: "violet",
    link: "/",
  },
  {
    title: "Robo Advisory",
    icon: Cpu,
    themeColor: "#0dd3ff",
    link: "/",
  },
  {
    title: "Global Market Access Tentative",
    icon: Globe,
    themeColor: "#57c0af",
    link: "/",
  },
];

const servicesData = [
  {
    mediaSrc: "/Arranging Custody.gif",
    title: "Arranging Custody",
    link: "/",
  },
  {
    mediaSrc: "/Arranging Deals in Investments.gif",
    title: "Arranging Deals in Investments",
    link: "/",
  },
  {
    mediaSrc: "/Managing assets1.gif",
    title: "Managing Assets",
    link: "/",
  },
  {
    mediaSrc: "/Arranging Credit & Advising on Credit.gif",
    title: "Arranging Credit & Advising on Credit",
    link: "/",
  },
  {
    mediaSrc: "/Legacy Planning & Family Office Management.gif",
    title: "Legacy Planning & Family Office Management",
    link: "/",
  },
  {
    mediaSrc: "/Advising on financial products 1.gif",
    title: "Advising on Financial Products",
    link: "/",
  },
];

// NavItem Component
function NavItem({
  href,
  children,
  isActive,
  onClick,
}: {
  href: string;
  children: ReactNode;
  isActive: boolean;
  onClick: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}) {
  return (
    <li className="px-4">
      <a
        href={href}
        onClick={onClick}
        className={`flex items-center gap-1 px-2 py-2 text-base text-xs ${isActive ? "text-[#57c0af]" : "text-white hover:text-[#57c0af]"
          }`}
      >
        {children}
      </a>
    </li>
  );
}

// Desktop Navigation
function DesktopNavigation({ onOpenUniverse }: { onOpenUniverse: () => void }) {
  const { activeTab, setActiveTab } = useActiveTab();
  const location = useLocation();
  const [isAdvisoryOpen, setIsAdvisoryOpen] = useState(false);
  const [isPriveOpen, setIsPriveOpen] = useState(false);

  const _firstNavRender = useRef(true);
  useEffect(() => {
    if (_firstNavRender.current) {
      _firstNavRender.current = false;
      return;
    }
    setActiveTab(location.pathname + location.hash);
  }, [location.pathname, location.hash, setActiveTab]);

  const handleScrollToSection = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    href: string
  ) => {
    event.preventDefault();

    const [path, hash] = href.split("#");
    if (path && window.location.pathname !== path) {
      window.location.href = `${path}#${hash}`;
    } else {
      const target = document.querySelector(`#${hash}`);
      target?.scrollIntoView({ behavior: "smooth" });
    }

    setActiveTab(href);
  };

  return (
    <nav className="hidden md:flex tracking-wider font-poppins z-50">
      <ul className="flex items-center -space-x-4 tracking-wider text-white text-[26px]">
        {/* Advisory dropdown */}
        <li className="px-4 relative" onMouseEnter={() => setIsAdvisoryOpen(true)} onMouseLeave={() => setIsAdvisoryOpen(false)}>
          <Link
            to="/"
            className={`flex items-center gap-1 px-2 py-2 text-base text-xs transition ${
              isAdvisoryOpen ||
              activeTab === "/#advisory" ||
              activeTab.includes("advisory") ||
              (activeTab !== "/" && DigitalWealthTabTitle.some((product) => activeTab === product.link))
                ? "text-[#57c0af]"
                : "text-white hover:text-[#57c0af]"
            } focus:outline-none`}
            onClick={(e) => handleScrollToSection(e, "/#advisory")}
          >
            Advisory
          </Link>
          <Transition
            show={isAdvisoryOpen}
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <div className="absolute left-0 z-50 mt-2 w-64 bg-black/95 backdrop-blur-xl border border-[#57c0af]/30 rounded-2xl shadow-2xl overflow-hidden">
              <div className="p-2">
                {DigitalWealthTabTitle.map((product, index) => {
                  const Icon = product.icon;
                  return (
                    <Link
                      key={index}
                      to={product.link}
                      className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition-all duration-200 group/item"
                      onClick={(e) => handleScrollToSection(e, "/#advisory")}
                    >
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center border border-white/10 group-hover/item:border-current transition-colors"
                        style={{ color: product.themeColor }}
                      >
                        <Icon size={16} strokeWidth={1.5} />
                      </div>
                      <span className="text-xs text-white group-hover/item:text-[#57c0af] transition-colors">
                        {product.title}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </Transition>
        </li>

        {/* Prive dropdown */}
        <li className="px-4 relative" onMouseEnter={() => setIsPriveOpen(true)} onMouseLeave={() => setIsPriveOpen(false)}>
          <Link
            to="/digitalwealth"
            className={`flex items-center gap-1 px-2 py-2 text-base text-xs transition ${
              isPriveOpen ||
              activeTab === "/#nuqiprive" ||
              activeTab.includes("prive") ||
              (activeTab !== "/" && servicesData.some((product) => activeTab === product.link))
                ? "text-[#57c0af]"
                : "text-white hover:text-[#57c0af]"
            } focus:outline-none`}
            onClick={(e) => handleScrollToSection(e, "/#nuqiprive")}
          >
            Prive
          </Link>
          <Transition
            show={isPriveOpen}
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <div className="absolute left-0 z-50 mt-2 w-64 bg-black/95 backdrop-blur-xl border border-[#57c0af]/30 rounded-2xl shadow-2xl overflow-hidden">
              <div className="p-2">
                {servicesData.map((product, index) => (
                  <Link
                    key={index}
                    to={product.link || "/"}
                    className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition-all duration-200 group/item"
                    onClick={(e) => handleScrollToSection(e, "/#nuqiprive")}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center border border-white/10 group-hover/item:border-current transition-colors overflow-hidden">
                      <img src={product.mediaSrc} alt={product.title} className="w-full h-full object-cover" />
                    </div>
                    <span className="text-xs text-white group-hover/item:text-[#57c0af] transition-colors">
                      {product.title}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </Transition>
        </li>

        <NavItem
          href="/iris"
          isActive={activeTab === "/iris"}
          onClick={() => setActiveTab("/iris")}>
          IRIS
        </NavItem>
        <NavItem
          href="/ethosphere"
          isActive={activeTab === "/ethosphere"}
          onClick={() => setActiveTab("/ethosphere")}>
          Ethosphere
        </NavItem>
        <NavItem
          href="/Disclosures"
          isActive={activeTab === "/Disclosures"}
          onClick={() => setActiveTab("/Disclosures")}>
          Disclosures
        </NavItem>
        <li className="px-4">
          <button
            type="button"
            onClick={onOpenUniverse}
            className="flex items-center gap-1 px-2 py-2 text-xs text-white hover:text-[#57c0af] transition"
          >
            Nuqi Universe
          </button>
        </li>
      </ul>
    </nav>
  );
}

// Mobile Navigation
function MobileNavigation({ onOpenUniverse }: { onOpenUniverse: () => void }) {
  const { activeTab, setActiveTab } = useActiveTab();
  const [isAdvisoryOpen, setIsAdvisoryOpen] = useState(false);
  const [isPriveOpen, setIsPriveOpen] = useState(false);

  const handleScrollToSection = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    href: string
  ) => {
    event.preventDefault();

    const [path, hash] = href.split("#");
    if (path && window.location.pathname !== path) {
      window.location.href = `${path}#${hash}`;
    } else {
      const target = document.querySelector(`#${hash}`);
      target?.scrollIntoView({ behavior: "smooth" });
    }

    setActiveTab(href);
  };

  return (
    <div className="lg:hidden font-poppins">
      <Popover>
        {() => (
          <>
            <Popover.Button>
              <Menu className="h-6 w-6 text-white" />
            </Popover.Button>
            <Transition.Root>
              <Transition.Child
                as={Fragment}
                enter="duration-150 ease-out"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="duration-150 ease-in"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Popover.Overlay className="fixed inset-0 bg-black bg-opacity-50" />
              </Transition.Child>
              <Transition.Child
                as={Fragment}
                enter="duration-150 ease-out"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="duration-150 ease-in"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Popover.Panel className="absolute top-0 left-0 w-full bg-black p-4">
                  <div className="flex justify-between items-center">
                    <Popover.Button>
                      <X className="h-6 w-6 text-white" />
                    </Popover.Button>
                  </div>
                  <nav className="mt-4 tracking-wide">
                    <ul className="space-y-4 tracking-wide">
                      {/* Advisory dropdown */}
                      <li className="px-4 relative" onMouseEnter={() => setIsAdvisoryOpen(true)} onMouseLeave={() => setIsAdvisoryOpen(false)}>
                        <button
                          onClick={() => {
                            setIsAdvisoryOpen(!isAdvisoryOpen);
                            setIsPriveOpen(false);
                          }}
                          className={`flex items-center gap-1 px-2 py-2 text-base text-xs transition ${
                            isAdvisoryOpen || DigitalWealthTabTitle.some(product => activeTab === product.link)
                              ? "text-[#57c0af]"
                              : "text-white hover:text-[#57c0af]"
                          } focus:outline-none`}
                        >
                          Advisory
                        </button>
                        <Transition
                          show={isAdvisoryOpen}
                          as={Fragment}
                          enter="transition ease-out duration-200"
                          enterFrom="opacity-0 translate-y-1"
                          enterTo="opacity-100 translate-y-0"
                          leave="transition ease-in duration-150"
                          leaveFrom="opacity-100 translate-y-0"
                          leaveTo="opacity-0 translate-y-1"
                        >
                          <div className="ml-4 mt-2 w-64 bg-black/95 backdrop-blur-xl border border-[#57c0af]/30 rounded-2xl shadow-2xl overflow-hidden">
                            <div className="p-2">
                              {DigitalWealthTabTitle.map((product, index) => {
                                const Icon = product.icon;
                                return (
                                  <Link
                                    key={index}
                                    to={product.link}
                                    className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition-all duration-200 group/item"
                                    onClick={(e) => handleScrollToSection(e, "/#advisory")}
                                  >
                                    <div
                                      className="w-8 h-8 rounded-lg flex items-center justify-center border border-white/10 group-hover/item:border-current transition-colors"
                                      style={{ color: product.themeColor }}
                                    >
                                      <Icon size={16} strokeWidth={1.5} />
                                    </div>
                                    <span className="text-xs text-white group-hover/item:text-[#57c0af] transition-colors">
                                      {product.title}
                                    </span>
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        </Transition>
                      </li>

                      {/* Prive dropdown */}
                      <li className="px-4 relative" onMouseEnter={() => setIsPriveOpen(true)} onMouseLeave={() => setIsPriveOpen(false)}>
                        <button
                          onClick={() => {
                            setIsPriveOpen(!isPriveOpen);
                            setIsAdvisoryOpen(false);
                          }}
                          className={`flex items-center gap-1 px-2 py-2 text-base text-xs transition ${
                            isPriveOpen || servicesData.some(product => activeTab === product.link)
                              ? "text-[#57c0af]"
                              : "text-white hover:text-[#57c0af]"
                          } focus:outline-none`}
                        >
                          Prive
                        </button>
                        <Transition
                          show={isPriveOpen}
                          as={Fragment}
                          enter="transition ease-out duration-200"
                          enterFrom="opacity-0 translate-y-1"
                          enterTo="opacity-100 translate-y-0"
                          leave="transition ease-in duration-150"
                          leaveFrom="opacity-100 translate-y-0"
                          leaveTo="opacity-0 translate-y-1"
                        >
                          <div className="ml-4 mt-2 w-64 bg-black/95 backdrop-blur-xl border border-[#57c0af]/30 rounded-2xl shadow-2xl overflow-hidden">
                            <div className="p-2">
                              {servicesData.map((product, index) => (
                                <Link
                                  key={index}
                                  to={product.link || "/"}
                                  className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition-all duration-200 group/item"
                                  onClick={(e) => handleScrollToSection(e, "/#nuqiprive")}
                                >
                                  <div className="w-8 h-8 rounded-lg flex items-center justify-center border border-white/10 group-hover/item:border-current transition-colors overflow-hidden">
                                    <img src={product.mediaSrc} alt={product.title} className="w-full h-full object-cover" />
                                  </div>
                                  <span className="text-xs text-white group-hover/item:text-[#57c0af] transition-colors">
                                    {product.title}
                                  </span>
                                </Link>
                              ))}
                            </div>
                          </div>
                        </Transition>
                      </li>

                      <NavItem href="/iris" isActive={false} onClick={() => setActiveTab("/iris")}>
                        IRIS
                      </NavItem>
                      <NavItem href="/ethosphere" isActive={false} onClick={() => setActiveTab("/ethosphere")}>
                        Ethosphere
                      </NavItem>
                      <NavItem href="/Disclosures" isActive={false} onClick={() => setActiveTab("/Disclosures")}>
                        Disclosures
                      </NavItem>

                      <li className="px-4">
                        <button
                          onClick={onOpenUniverse}
                          className="px-2 py-2 text-xs text-white hover:text-[#57c0af] transition-colors tracking-wider"
                        >
                          Nuqi Universe
                        </button>
                      </li>
                    </ul>
                  </nav>

                  {/* Mobile Sign Up & Login Links */}
                  <div className="mt-6">
                    <a href="http://portal.nuqiwealth.in/" target="_blank" rel="noopener noreferrer">
                      <button className="w-full px-4 py-2 text-sm font-semibold text-black bg-[#57c0af] rounded-xl whitespace-nowrap hover:text-black">
                        Sign Up
                      </button>
                    </a>

                    <a href="http://portal.nuqiwealth.in/" target="_blank" rel="noopener noreferrer">
                      <button className="w-full mt-3 px-4 py-2 text-sm font-semibold text-white bg-transparent border border-[#57c0af] rounded-xl hover:bg-[#57c0af] hover:text-black">
                        Log In
                      </button>
                    </a>
                  </div>
                </Popover.Panel>
              </Transition.Child>
            </Transition.Root>
          </>
        )}
      </Popover>
    </div>
  );
}

function CountrySelect() {
  return (
    <div className="flex items-center justify-center">
      <Select
        onValueChange={(value) => {
          if (value === "india") {
            window.location.href = "https://in.nuqiwealth.com/";
          } else {
            window.location.href = "https://uae.nuqiwealth.com/";
          }
        }}
      >
        <SelectTrigger className="w-full bg-black text-white border border-[#57c0af] rounded-xl hover:bg-[#57c0af] hover:text-black transition-all duration-200">
          <div className="flex items-center text-center">
            <img
              className="w-6 h-6 object-contain"
              src="/india.jpg"
              alt="India"
            />
          </div>
        </SelectTrigger>
        <SelectContent className="bg-black text-white border border-[#57c0af] rounded-xl">
          <SelectItem value="india">
            <div className="flex items-center">
              <img className="w-6 h-6 object-contain mr-2" src="/india.jpg" alt="India" />
              India
            </div>
          </SelectItem>
          <SelectItem value="uae">
            <div className="flex items-center">
              <img className="w-6 h-6 object-contain mr-2" src="/uae.jpg" alt="UAE" />
              UAE
            </div>
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

// Home Logo Component
function HomeLogo() {
  const handleScrollToSection = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    href: string
  ) => {
    event.preventDefault();

    const [path, hash] = href.split("#");
    if (path && window.location.pathname !== path) {
      window.location.href = `${path}#${hash}`;
    } else {
      const target = document.querySelector(`#${hash}`);
      target?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Link to="/" className="block">
      <img
        src="/white-in-logo-cropped.png"
        alt="Nuqi Logo"
        className="h-8 sm:h-10 md:h-8 w-auto lg:ml-20 object-contain"
        onClick={(e) => handleScrollToSection(e, "/#hero")}
      />
    </Link>
  );
}

// Main Navbar Component
export function Navbar() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [navbarHeight, setNavbarHeight] = useState(56);
  const navbarRef = useRef<HTMLElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (navbarRef.current) {
      setNavbarHeight(navbarRef.current.offsetHeight);
    }
  }, []);

  return (
    <>
      <header ref={navbarRef} className="fixed top-0 w-full h-[56px] z-50 bg-black backdrop-blur-xl bg-opacity-50 border-opacity-50 border-b-[1px] border-[#4d4d4d] shadow-lg">
        <div className="container mx-auto px-4 h-full flex items-center justify-between font-poppins">
          {/* Logo */}
          <div className="flex-shrink-0">
            <HomeLogo />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex flex-grow justify-center">
            <DesktopNavigation onOpenUniverse={() => setSidebarOpen(true)} />
          </div>

          {/* Right Section */}
          <div className="flex items-center space-x-3 lg:space-x-4 lg:mr-5">
            {/* User Icon with Dropdown for Mobile */}
            <div className="lg:hidden relative">
              <Popover>
                <Popover.Button>
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-black border border-[#57c0af] text-white cursor-pointer">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6"
                      fill="white"
                      viewBox="0 0 24 24"
                      stroke="none"
                    >
                      <path d="M12 12c2.28 0 4-1.72 4-4s-1.72-4-4-4-4 1.72-4 4 1.72 4 4 4zm0 2c-3.31 0-10 1.67-10 5v1h20v-1c0-3.33-6.69-5-10-5z" />
                    </svg>
                  </div>
                </Popover.Button>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Popover.Panel className="absolute z-10 justify-center items-center -mr-6 w-24 h-16 sm:-mt-3 bg-black border border-[#57c0af] rounded-2xl shadow-lg top-full right-0">
                    <div className="flex flex-col py-1">
                      <a href="http://portal.nuqiwealth.in/" target="_blank" rel="noopener noreferrer">
                        <button className="flex items-center px-4 py-1 text-sm text-white hover:bg-[#57c0af] hover:text-black">
                          <span className="ml-2">Log In</span>
                        </button>
                      </a>
                      <a href="http://portal.nuqiwealth.in/" target="_blank" rel="noopener noreferrer">
                        <button className="flex items-center px-4 py-1 text-sm text-white hover:bg-[#57c0af] hover:text-black">
                          <span className="ml-2">Sign Up</span>
                        </button>
                      </a>
                    </div>
                  </Popover.Panel>
                </Transition>
              </Popover>
            </div>

            {/* Mobile Navigation */}
            <div className="lg:hidden order-3">
              <MobileNavigation onOpenUniverse={() => setSidebarOpen(true)} />
            </div>
            <div className="lg:hidden">
              <CountrySelect />
            </div>

            {/* Desktop Login/Signup */}
            <div className="hidden lg:flex space-x-4 order-2">
              <a href="http://portal.nuqiwealth.in/" target="_blank" rel="noopener noreferrer">
                <button className="px-3 py-2 text-sm font-semibold text-white bg-transparent border border-[#57c0af] rounded-xl hover:bg-[#57c0af] hover:text-black">
                  Log In/Sign Up
                </button>
              </a>

              <div className="flex lg:ml-4">
                <CountrySelect />
              </div>
            </div>
          </div>
        </div>
      </header>

      <NuqiUniverseSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        navbarHeight={navbarHeight}
      />
    </>
  );
}
